import React, { useState, useMemo, useRef, useEffect, useCallback } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import ForgottenPassword from './ForgottenPassword.jsx'
import LoginPanel from './LoginPanel.jsx'
import ResetPassword from './ResetPassword.jsx'
import { cancelTokenRefresh } from 'actions/authActions'
import { push } from 'Helper/routeHelpers'
import Waiting from '../Waiting.jsx'
import AuthError from '../AuthError.jsx'

const SummitAuth = ({
  retryAccessTokenGrant,
  setSummitLoginRequired,
  isPasswordReset,
  isEmailVerification,
  isAccountSetup,
  queryParams,
  setMessage,
  message,
  subProvider
}) => {
  const dispatch = useDispatch()
  const [panelState, setPanelState] = useState('login')
  const auth = useSelector(state => state.auth)

  const setStateMessage = useCallback((state, message) => {
    if (state === 'login') {
      setMessage(message)
      push('/login')
    } else if (state === 'summit-login') {
      setSummitLoginRequired(true)
      const path =
        subProvider && subProvider !== ''
          ? `/${subProvider}/summit/login`
          : '/summit/login'
      push(path)
    }
    setPanelState(state)
    setMessage(message)
  }, [subProvider, setMessage, setSummitLoginRequired])

  const setStateMessageRef = useRef(setStateMessage)

  useEffect(() => {
    // Keep the ref aligned with the latest callback to avoid stale closures.
    setStateMessageRef.current = setStateMessage
  }, [setStateMessage])

  useEffect(() => {
    return () => {
      setStateMessageRef.current = () => null
    }
  }, [])

  const newPanelState = useMemo(() => {
    if (panelState !== 'token-refresh' && auth.refreshInProgress) {
      if (panelState !== 'token-refresh') {
        return 'token-refresh'
      }
    } else if (panelState === 'token-refresh' && !auth.refreshInProgress) {
      if (panelState !== 'login') {
        return 'login'
      }
    }

    if (isPasswordReset) {
      // password reset can push back to recover-password but url params will still cause
      // isPasswordReset to be true... so we had to check for that explicitly.

      // this is a password reset
      if (
        panelState !== 'reset-password' &&
        panelState !== 'summit-login' &&
        panelState !== 'recover-password'
      ) {
        return 'reset-password'
      }
    } else if (isAccountSetup) {
      if (panelState !== 'verify-and-setup' && panelState !== 'summit-login') {
        return 'verify-and-setup'
      }
    } else if (isEmailVerification) {
      if (panelState !== 'verify' && panelState !== 'summit-login') {
        return 'verify'
      }
    }
  }, [panelState, isPasswordReset, auth.refreshInProgress])

  useEffect(() => {
    if (panelState !== newPanelState && newPanelState != null) {
      setPanelState(newPanelState)
    }
  }, [panelState, newPanelState])

  // This component is loaded with summit is acting as the auth provider.
  switch (panelState) {
    case 'token-refresh':
      return (
        <Waiting
          message="Attempting to restore session..."
          extra={
            <button
              aria-label="cancel button"
              className="btn btn-default refresh-cancel"
              onClick={() => dispatch(cancelTokenRefresh)}
            >
              Cancel
            </button>
          }
        />
      )
    case 'recover-password':
      return (
        <ForgottenPassword
          message={message}
          setStateMessageRef={setStateMessageRef}
        />
      )
    case 'recovery-submitting':
      return <Waiting message="Sending recovery request..." />
    case 'reset-password':
    case 'verify':
    case 'verify-and-setup':
      return (
        <ResetPassword
          queryParams={queryParams}
          message={message}
          setStateMessageRef={setStateMessageRef}
        />
      )
    case 'error':
      return <AuthError error={message} />
    case 'login':
    case 'recovery-sent':
    default:
      return (
        <LoginPanel
          message={message}
          setSummitLoginRequired={setSummitLoginRequired}
          setStateMessageRef={setStateMessageRef}
          retryAccessTokenGrant={retryAccessTokenGrant}
        />
      )
  }
}

export default SummitAuth
